import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Zap } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useDirectoryProviders, useTestProvider, type DirectoryProvider } from "@/lib/directory";
import { formatDateTime } from "@/lib/format";
import { SectionHeader } from "@/components/application/section-headers/section-headers";

/**
 * Directory (LDAP) — read-only přehled nakonfigurovaných providerů + test spojení.
 * Providery se konfigurují přes env/config (ne runtime CRUD).
 */
export function DirectoryPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useDirectoryProviders();

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("directory.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("directory.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data && data.length === 0 && (
        <div className="rounded-xl bg-secondary p-6 text-center text-sm text-tertiary ring-1 ring-secondary">
          {t("directory.none")}
        </div>
      )}

      {data && data.length > 0 && (
        <div className="flex flex-col gap-4">
          {data.map((p) => (
            <ProviderCard key={p.id} provider={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProviderCard({ provider }: { provider: DirectoryProvider }) {
  const { t } = useTranslation();
  const test = useTestProvider();
  const [result, setResult] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const runTest = async () => {
    setResult(null);
    try {
      const r = await test.mutateAsync(provider.id);
      const success = r.success === true || r.available === true;
      setOk(success);
      setResult(String(r.message ?? (success ? t("directory.ok") : t("directory.fail"))));
    } catch {
      setOk(false);
      setResult(t("directory.fail"));
    }
  };

  return (
    <div className="rounded-xl bg-primary p-4 ring-1 ring-secondary">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-md font-semibold text-primary">{provider.id}</span>
          <Badge type="pill-color" size="sm" color={provider.available ? "success" : "error"}>
            {provider.available ? t("directory.available") : t("directory.unavailable")}
          </Badge>
          <span className="text-xs text-tertiary">{provider.type}</span>
        </div>
        <Button size="sm" color="secondary" iconLeading={Zap} isDisabled={test.isPending} onPress={() => void runTest()}>
          {test.isPending ? t("connectors.testing") : t("connectors.test")}
        </Button>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <Kv k="URL" v={provider.url} />
        <Kv k="Base DN" v={provider.baseDn} />
        <Kv k="Bind DN" v={provider.bindDn} />
        <Kv k={t("directory.filter")} v={provider.userSearchFilter} />
        <Kv k={t("directory.lastSuccess")} v={formatDateTime(provider.lastSuccessAt)} />
        <Kv k={t("directory.lastFailure")} v={provider.lastFailureReason ?? formatDateTime(provider.lastFailureAt)} />
      </dl>

      {result && (
        <div className={`mt-3 rounded-lg bg-secondary px-3 py-2 text-sm ring-1 ring-secondary ${ok ? "text-success-primary" : "text-error-primary"}`}>
          {ok ? "✓ " : "✗ "}
          {result}
        </div>
      )}
    </div>
  );
}

function Kv({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-tertiary">{k}:</dt>
      <dd className="min-w-0 truncate font-mono text-xs text-primary">{v || "—"}</dd>
    </div>
  );
}
